// hooks/useRPM.js
import { useState, useRef } from "react";
import { Motion } from "@capacitor/motion";
import { Capacitor } from "@capacitor/core";

function autocorrPeak(samples, fs, minHz = 0.6, maxHz = 2.3) {
  const n = samples.length;
  if (n < fs * 2) return null;

  const mean = samples.reduce((a, b) => a + b, 0) / n;
  const x = samples.map((s) => s - mean);
  const energy = Math.sqrt(x.reduce((a, b) => a + b * b, 0)) + 1e-9;

  const minLag = Math.floor(fs / maxHz);
  const maxLag = Math.floor(fs / minHz);

  let bestLag = 0;
  let bestVal = 0;

  for (let lag = minLag; lag <= maxLag; lag++) {
    let num = 0;
    for (let i = 0; i < n - lag; i++) {
      num += x[i] * x[i + lag];
    }

    const r = num / (energy * energy);
    if (r > bestVal) {
      bestVal = r;
      bestLag = lag;
    }
  }

  return bestLag > 0 ? { freq: fs / bestLag, r: bestVal } : null;
}

export const useRPM = (initialCalibration = 1.0) => {
  const [rpm, setRpm] = useState(0);
  const [started, setStarted] = useState(false);
  const [calibration, setCalibration] = useState(initialCalibration);

  const accelListenerRef = useRef(null);
  const lastT = useRef(null);
  const accelBuf = useRef([]);
  const gyroBuf = useRef([]);

  const ema = useRef(null);
  const history = useRef([]);
  const displayRpmRef = useRef(0);
  const lastUpdateRef = useRef(0);

  // 🔹 nuevas refs anti-ruido
  const lastGoodRRef = useRef(0);
  const lowConfidenceSinceRef = useRef(null);
  const rejectedCountRef = useRef(0);

  // 🔧 Parámetros afinados
  const HISTORY_LEN = 3;
  const EMA_ALPHA = 0.16;
  const HYSTERESIS = 3;
  const MIN_RPM = 32;
  const UPDATE_INTERVAL = 220;
  const MAX_JUMP = 14;
  const MOTION_STDDEV = 0.08;

  // 🔹 confianza
  const MIN_CONFIDENCE = 0.20;
  const STRONG_CONFIDENCE = 0.30;
  const HOLD_MS = 500;

  // 📊 Autocalibración
  const CALIB_WINDOW = 8000; // 8s
  const calibBuffer = useRef([]);
  const lastCalibTime = useRef(0);

  const requestMotionPermission = async () => {
    if (Capacitor.isNativePlatform()) return true;

    if (
      typeof DeviceMotionEvent !== "undefined" &&
      typeof DeviceMotionEvent.requestPermission === "function"
    ) {
      try {
        const response = await DeviceMotionEvent.requestPermission();
        return response === "granted";
      } catch (err) {
        console.error("❌ Error permisos:", err);
        return false;
      }
    }

    return true;
  };

  // === Autocalibración con clamp global + suavizado ===
  function autoCalibrate(newRpm, rpmRaw) {
    const now = Date.now();
    calibBuffer.current.push({ t: now, v: rpmRaw });

    while (
      calibBuffer.current.length > 0 &&
      now - calibBuffer.current[0].t > CALIB_WINDOW
    ) {
      calibBuffer.current.shift();
    }

    if (calibBuffer.current.length < 10) return;

    const values = calibBuffer.current.map((d) => d.v);
    const avgRaw = values.reduce((a, b) => a + b, 0) / values.length;
    const variance =
      values.reduce((a, b) => a + (b - avgRaw) ** 2, 0) / values.length;
    const stddev = Math.sqrt(variance);

    if (
      stddev < 1.5 &&
      avgRaw > MIN_RPM &&
      now - lastCalibTime.current > 10000
    ) {
      const rawFactor = newRpm > 0 ? newRpm / avgRaw : 1.0;

      // limitar cambios por ciclo ±5%
      const clamped = Math.max(0.95, Math.min(1.05, rawFactor));

      // suavizado fuerte
      const newCalibration = calibration * 0.95 + clamped * 0.05;

      // clamp global 0.7–1.3
      const bounded = Math.max(0.7, Math.min(1.3, newCalibration));

      setCalibration(bounded);
      lastCalibTime.current = now;

      console.log(
        `✅ Autocalibrado: ${bounded.toFixed(3)} (rawFactor=${rawFactor.toFixed(3)})`
      );
    }
  }

  function processSample(tNow, accelMag, rotationRate) {
    let fs = 60;

    if (lastT.current != null) {
      const dt = (tNow - lastT.current) / 1000;
      fs = 1 / Math.max(dt, 1e-3);
    }

    lastT.current = tNow;

    accelBuf.current.push(accelMag);
    gyroBuf.current.push(rotationRate ? rotationRate.alpha || 0 : 0);

    const maxLen = Math.max(144, Math.floor(fs * 4.5));

    if (accelBuf.current.length > maxLen) {
      accelBuf.current.splice(0, accelBuf.current.length - maxLen);
    }

    if (gyroBuf.current.length > maxLen) {
      gyroBuf.current.splice(0, gyroBuf.current.length - maxLen);
    }

    if (accelBuf.current.length !== maxLen) return;

    const mean =
      accelBuf.current.reduce((a, b) => a + b, 0) / accelBuf.current.length;

    const variance =
      accelBuf.current.reduce((a, b) => a + (b - mean) ** 2, 0) /
      accelBuf.current.length;

    const stddev = Math.sqrt(variance);

    // 🔹 si no hay movimiento claro, apagamos rpm
    if (stddev < MOTION_STDDEV) {
      if (Date.now() - lastUpdateRef.current > UPDATE_INTERVAL) {
        setRpm(0);
        displayRpmRef.current = 0;
        ema.current = null;
        history.current = [];
        lowConfidenceSinceRef.current = null;
        rejectedCountRef.current = 0;
        lastGoodRRef.current = 0;
        lastUpdateRef.current = Date.now();
      }
      return;
    }

    const resAccel = autocorrPeak(accelBuf.current, fs, 0.6, 2.3);
    const resGyro = autocorrPeak(gyroBuf.current, fs, 0.6, 2.3);

    let rpmRaw = null;
    let confidence = 0;

    // 🔹 combinar sensores de forma más estable
    if (resAccel && resGyro) {
      confidence = Math.max(resAccel.r, resGyro.r);

      const diffR = Math.abs(resAccel.r - resGyro.r);
      const closeEnough = Math.abs(resAccel.freq - resGyro.freq) * 60 < 8;

      if (closeEnough) {
        rpmRaw = ((resAccel.freq + resGyro.freq) / 2) * 60;
        confidence = (resAccel.r + resGyro.r) / 2;
      } else if (diffR < 0.06) {
        rpmRaw = null;
      } else {
        rpmRaw = (resAccel.r > resGyro.r ? resAccel.freq : resGyro.freq) * 60;
      }
    } else if (resAccel) {
      rpmRaw = resAccel.freq * 60;
      confidence = resAccel.r;
    } else if (resGyro) {
      rpmRaw = resGyro.freq * 60;
      confidence = resGyro.r;
    }

    if (rpmRaw == null) return;

    // aplicar calibración y clamp rpm
    rpmRaw *= calibration;
    rpmRaw = Math.max(30, Math.min(140, rpmRaw));

    if (rpmRaw < MIN_RPM) return;

    const now = Date.now();

    // 🔹 si la confianza es baja, mantener el valor bueno un rato
    if (confidence < MIN_CONFIDENCE) {
      if (displayRpmRef.current > 0) {
        if (lowConfidenceSinceRef.current == null) {
          lowConfidenceSinceRef.current = now;
        }

        if (now - lowConfidenceSinceRef.current < HOLD_MS) {
          return;
        }
      }

      return;
    }

    lowConfidenceSinceRef.current = null;
    lastGoodRRef.current = confidence;

    // 🔹 suavizado EMA
    ema.current =
      ema.current == null
        ? rpmRaw
        : ema.current + EMA_ALPHA * (rpmRaw - ema.current);

    history.current.push(ema.current);
    if (history.current.length > HISTORY_LEN) {
      history.current.shift();
    }

    let newRpm =
      history.current.reduce((a, b) => a + b, 0) / history.current.length;

    newRpm = Math.round(newRpm);

    // 🔹 predictor muy suave
    const trend = newRpm - displayRpmRef.current;
    if (Math.abs(trend) > 0) {
      newRpm = Math.round(newRpm + 0.15 * trend);
    }

    // 🔹 anti-saltos inteligente
    if (displayRpmRef.current > 0) {
      const delta = Math.abs(newRpm - displayRpmRef.current);

      if (delta > MAX_JUMP) {
        rejectedCountRef.current += 1;

        // solo aceptar salto fuerte si viene muy respaldado
        if (confidence < STRONG_CONFIDENCE || rejectedCountRef.current < 2) {
          return;
        }
      } else {
        rejectedCountRef.current = 0;
      }
    }

    if (
      now - lastUpdateRef.current > UPDATE_INTERVAL &&
      Math.abs(newRpm - displayRpmRef.current) >= HYSTERESIS
    ) {
      displayRpmRef.current = newRpm;
      setRpm(displayRpmRef.current);
      lastUpdateRef.current = now;

      autoCalibrate(newRpm, rpmRaw);
    }
  }

  const startDetection = async () => {
    if (started) return;

    const granted = await requestMotionPermission();
    if (!granted) {
      alert("❌ No hay permisos de sensores.");
      return;
    }

    const handler = (event) => {
      const data = event.accelerationIncludingGravity || event.acceleration;
      if (!data) return;

      const { x = 0, y = 0, z = 0 } = data;
      const accelMag = Math.sqrt(x * x + y * y + z * z);
      const t = event.timestamp ?? Date.now();

      processSample(t, accelMag, event.rotationRate);
    };

    if (Capacitor.isNativePlatform()) {
      accelListenerRef.current = await Motion.addListener("accel", handler);
    } else {
      window.addEventListener("devicemotion", handler);
      accelListenerRef.current = {
        remove: () => window.removeEventListener("devicemotion", handler),
      };
    }

    setStarted(true);
  };

  const stopDetection = () => {
    if (accelListenerRef.current) {
      accelListenerRef.current.remove();
      accelListenerRef.current = null;
    }

    setStarted(false);
    setRpm(0);

    accelBuf.current = [];
    gyroBuf.current = [];
    ema.current = null;
    history.current = [];
    displayRpmRef.current = 0;
    lastUpdateRef.current = 0;
    calibBuffer.current = [];
    lastT.current = null;

    // 🔹 reset refs nuevas
    lastGoodRRef.current = 0;
    lowConfidenceSinceRef.current = null;
    rejectedCountRef.current = 0;
  };

  return { rpm, started, calibration, startDetection, stopDetection };
};